// libraries
// react
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
// routing
import { useNavigate } from '@tanstack/react-router';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import {
  type GetEnergyGroupDto,
  type GetEnergyInitiativeDto,
  usePutEnergyGroupInitiativeMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFTextField } from '@/components/ui/form';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  toInitiativeFormValues,
  toPostEnergyInitiativeDto,
} from './initiativeForm.mappers';
import { createInitiativeFormSchema } from './initiativeForm.schema';
import InitiativePeriodsSection from './InitiativePeriodsSection';
import type {
  InitialInitiativeFetch,
  InitiativePeriodsSectionHandle,
} from './InitiativePeriodsSection';
import InitiativeSourceSelector from './InitiativeSourceSelector';

import { zodResolver } from '@hookform/resolvers/zod';

export default function EditInitiativeForm({
  energyGroup,
  initiative,
  onCancel,
}: {
  energyGroup?: GetEnergyGroupDto;
  initiative: GetEnergyInitiativeDto;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [putEnergyInitiative, { isLoading: isSaving }] =
    usePutEnergyGroupInitiativeMutation();

  const periodsRef = useRef<InitiativePeriodsSectionHandle>(null);

  const schema = createInitiativeFormSchema(t);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toInitiativeFormValues(initiative),
    mode: 'onTouched',
  });

  const energyTreeNodes = useWatch({ control, name: 'energyTreeNodes' });

  const initialFetch: InitialInitiativeFetch | null =
    Array.isArray(initiative.energyTreeNodes) &&
    initiative.energyTreeNodes.length > 0 &&
    (initiative.baselineStart || initiative.comparisonStart)
      ? {
          nodeIds: initiative.energyTreeNodes,
          start: Math.min(
            ...[initiative.baselineStart, initiative.comparisonStart]
              .filter((iso): iso is string => Boolean(iso))
              .map((iso) => new Date(iso).getTime()),
          ),
          end: Math.max(
            ...[initiative.baselineEnd, initiative.comparisonEnd]
              .filter((iso): iso is string => Boolean(iso))
              .map((iso) => new Date(iso).getTime()),
          ),
        }
      : null;

  const onSubmit = handleSubmit(async (values) => {
    if (!energyGroup?.energyGroupId || !initiative.energyInitiativeId) return;

    const result = await putEnergyInitiative({
      groupId: energyGroup.energyGroupId,
      initiativeId: initiative.energyInitiativeId,
      putEnergyInitiativeDto: toPostEnergyInitiativeDto(values),
    });
    if ('error' in result) return;

    dispatch(
      showSnackbar({
        message: t('common.updateSuccess', { ns: 'forms' }),
        severity: 'success',
      }),
    );
    navigate({ to: '../../' });
    // Errors are handled by apiErrorMiddleware (snackbar + App Insights).
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="initiative-edit-form">
      <Stack spacing={2}>
        <Card variant="outlined" data-testid="initiative-edit-card">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                label={t('createInitiative.name.label', { ns: 'forms' })}
                placeholder={t('createInitiative.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createInitiative.name.helperText', {
                  ns: 'forms',
                })}
                disabled={isSaving}
                data-testid="edit-initiative-name-input"
              />

              <RHFTextField
                name="description"
                control={control}
                fullWidth
                multiline
                minRows={2}
                label={t('createInitiative.description.label', {
                  ns: 'forms',
                })}
                placeholder={t('createInitiative.description.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createInitiative.description.helperText', {
                  ns: 'forms',
                })}
                disabled={isSaving}
                data-testid="initiative-edit-description-input"
              />

              <InitiativeSourceSelector
                control={control}
                nodes={energyGroup?.energyTreeNodes ?? []}
                disabled={isSaving}
                onSourcesBlur={(nodeIds) =>
                  periodsRef.current?.triggerAutoFetch(nodeIds)
                }
              />

              {energyTreeNodes.length > 0 && (
                <InitiativePeriodsSection
                  ref={periodsRef}
                  control={control}
                  setValue={setValue}
                  energyGroupId={energyGroup?.energyGroupId}
                  accountId={energyGroup?.accountId}
                  totalNodesInGroup={energyGroup?.energyTreeNodes?.length ?? 0}
                  disabled={isSaving}
                  initialFetch={initialFetch}
                />
              )}
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isSaving}
            data-testid="initiative-edit-cancel-button"
          >
            {t('common.cancel', { ns: 'forms' })}
          </CancelButton>
          <SubmitButton
            loading={isSaving}
            disabled={isSaving || !isValid || !isDirty}
          >
            {isSaving
              ? t('editInitiative.submitting', { ns: 'forms' })
              : t('editInitiative.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
